import React, { useRef, useState } from "react";
import { Icon } from "./Icon";
import clsx from "clsx";

export const FolderUploadField = ({
  onChange,
  className,
  size = "lg",
}: {
  onChange: (files: FileList) => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onChange(files);
    }
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const items = event.dataTransfer.items;
    if (!items) return;

    // Helper to recursively traverse directories
    const getFilesFromEntry = (entry: any, path = ""): Promise<File[]> => {
      return new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((file: File) => {
            // Patch the file with a webkitRelativePath property for compatibility
            Object.defineProperty(file, "webkitRelativePath", {
              value: path + entry.name,
            });
            resolve([file]);
          });
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          dirReader.readEntries(async (entries: any[]) => {
            const promises = entries.map((e) => getFilesFromEntry(e, path + entry.name + "/"));
            const results = await Promise.all(promises);
            resolve(results.flat());
          });
        } else {
          resolve([]);
        }
      });
    };

    // Collect all files from dropped items
    const filePromises: Promise<File[]>[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
      if (entry) {
        filePromises.push(getFilesFromEntry(entry));
      }
    }
    const filesNested = await Promise.all(filePromises);
    const files = filesNested.flat();
    if (files.length > 0) {
      // Create a FileList-like object
      const dataTransfer = new DataTransfer();
      files.forEach((file) => dataTransfer.items.add(file));
      onChange(dataTransfer.files);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  return (
    <div
      className={clsx(
        "relative w-full flex items-center justify-center",
        "rounded-lg transition-all duration-200 border-2 border-dashed",
        {
          "h-32": size === "sm",
          "h-40": size === "md",
          "h-52": size === "lg",
          "border-brand dark:border-brand-light bg-brand/10 dark:bg-brand/20": isDragOver,
          "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50": !isDragOver,
        },
        className,
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      // onClick={() => fileInputRef.current?.click()}
      style={{ cursor: "pointer" }}
    >
      <div className="absolute flex flex-col items-center justify-center gap-2 w-full h-full top-0 left-0 pointer-events-none">
        <Icon
          name="upload"
          size={size === "lg" ? 32 : size === "md" ? 24 : 20}
          className="text-gray-400 dark:text-gray-500"
        />
        <span className="text-xs text-gray-500 dark:text-gray-400">Drag & Drop Folder</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">- or -</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">Click to Select Folder</span>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        {...({ webkitdirectory: "", directory: "" } as any)}
        multiple
        onChange={handleFileSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        accept=".py,.js,.ts,.jsx,.tsx,.json,.html,.css,.md,.sql,.xml,.txt"
        tabIndex={-1}
        style={{ pointerEvents: "auto" }}
      />
    </div>
  );
};
